import { Box, Typography } from "@mui/material";
import React, { useState } from "react";
import { useMutation } from "react-query";
import ChooseStockCounts from "../BasicInventory/ChooseStockCounts";
import Scope from "./Scope";
import CostColumnOptions from "./CostColumnOptions";
import ReportingFooter from "../../ReportingFooter";
import inventoryClient from "../../../../../clients/inventoryClient";
import {
  countReportScopes,
  stockCountReportTypes,
} from "../../../../../clients/inventoryObjects";
import utilFunctions from "../../../../../utilities/utilFunctions";
import InsufficientReportDataModal from "../../../../../components/Modals/InsufficientReportDataModal";

const scopeOptions = [
  { key: "Full Count", value: countReportScopes.overall },
  { key: "Records by Area", value: countReportScopes.byArea },
];

const CountReport = ({
  establishmentId,
  pastStockCounts,
  isLoadingPastStockCounts,
  setPage,
  setAlertSuccess,
  setErrorMessage,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) => {
  const [countForReport, setCountForReport] = useState(null);
  const [scope, setScope] = useState(countReportScopes.overall);
  const [includeDefaultPuCost, setIncludeDefaultPuCost] = useState(false);
  const [includeDefaultPuPerUnitCost, setIncludeDefaultPuPerUnitCost] =
    useState(false);
  const [isEmptyResultModalOpen, toggleIsEmptyResultModalOpen] = useState(false);

  const generateMutation = useMutation(inventoryClient.generateStockCountReport, {
    onSuccess: (response) => {
      if (response.status === 204) {
        toggleIsEmptyResultModalOpen(true);
      } else {
        utilFunctions.downloadFile(
          response?.data?.report?.stockCountReportMetadata?.s3PresignedUrl
        );
        setAlertSuccess(
          "Report generated successfully, your download should have started."
        );
      }
    },
    onError: () => {
      setErrorMessage("Report generation failed!");
    },
  });

  const handleGenerateReport = () => {
    generateMutation.mutate({
      establishmentId,
      stockCountId: countForReport.id,
      reportType: stockCountReportTypes.countReport,
      countReportFilters: {
        scope,
        includeDefaultPuCost,
        includeDefaultPuPerUnitCost,
      },
    });
  };

  return (
    <>
      <Box>
        <Box sx={{ marginTop: 8 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              height: "fit-content",
              width: "70%",
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.terrain[200],
              border: "1px solid",
              borderColor: (theme) => theme.palette.terrain[300],
              padding: 2,
            }}
          >
            <Typography variant="body2">
              This report exports the records of a completed stock count. Choose
              &quot;Full Count&quot; for one row per product across the whole
              count, or &quot;Records by Area&quot; to break each product out by
              the area it was counted in.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ marginTop: 8 }}>
          <ChooseStockCounts
            setPage={setPage}
            isLoading={isLoadingPastStockCounts}
            pastStockCounts={pastStockCounts}
            setCountForReport={setCountForReport}
            countForReport={countForReport}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </Box>
        <Box sx={{ marginTop: 8 }}>
          <Scope
            scopeOptions={scopeOptions}
            selectedScope={scope}
            setSelectedScope={setScope}
          />
        </Box>
        <Box sx={{ marginTop: 8 }}>
          <CostColumnOptions
            includeDefaultPuCost={includeDefaultPuCost}
            toggleIncludeDefaultPuCost={setIncludeDefaultPuCost}
            includeDefaultPuPerUnitCost={includeDefaultPuPerUnitCost}
            toggleIncludeDefaultPuPerUnitCost={setIncludeDefaultPuPerUnitCost}
          />
        </Box>
      </Box>
      <ReportingFooter
        buttonText={"Create Report"}
        disableButton={countForReport === null || countForReport === undefined}
        submit={handleGenerateReport}
        isLoading={generateMutation?.isLoading}
      />
      <InsufficientReportDataModal
        isOpen={isEmptyResultModalOpen}
        toggleModalOpen={toggleIsEmptyResultModalOpen}
        loading={false}
      />
    </>
  );
};

export default CountReport;
